import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { eventType } from "@/lib/constants";
import { DisplayMusicByToken } from "@/components/DisplayMusic";
import CinemaBrand from "@/components/CinemaBrand";
import { Clock, MapPin, Film } from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";

/**
 * Programm-Anzeige wie in großen Kinos — Marquee-Board, keine Interaktion.
 * Layout: Marquee-Header mit Uhrzeit → Große Datums-Sektionen → sanftes Auto-Scroll,
 * jede Show mit Poster, Titel, Genre, Dauer, FSK, Saal & Startzeit-Chip.
 */
export default function ProgramDisplay() {
  const { token } = useParams();
  const [d, setD] = useState(null);
  const [now, setNow] = useState(new Date());
  const scrollRef = useRef(null);

  useEffect(() => {
    const load = () => api.get(`/displays/public/${token}`).then((r) => setD(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 60_000);
    const c = setInterval(() => setNow(new Date()), 1000);
    return () => { clearInterval(t); clearInterval(c); };
  }, [token]);

  const byDate = useMemo(() => {
    const map = {};
    for (const s of (d?.upcoming || [])) {
      (map[s.date] = map[s.date] || []).push(s);
    }
    return map;
  }, [d]);
  const dates = useMemo(() => Object.keys(byDate).sort(), [byDate]);

  // Sanftes Auto-Scroll bei Überlauf (Cinema-Board-Charakter)
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    let raf;
    let dir = 1;
    let last = performance.now();
    const step = (t) => {
      const dt = t - last;
      last = t;
      const max = el.scrollHeight - el.clientHeight;
      if (max > 4) {
        el.scrollTop += dir * dt * 0.02; // ~20 px/s
        if (el.scrollTop >= max - 1) dir = -1;
        else if (el.scrollTop <= 1) dir = 1;
      }
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [d]);

  if (!d) return <div className="min-h-screen bg-black flex items-center justify-center text-[#A8A398]">Lade Programm …</div>;

  return (
    <>
      <DisplayMusicByToken token={token} autoStart hideToggle />
      <div className="min-h-screen w-full bg-black text-[#F5F0E6] relative overflow-hidden font-body">
        {/* Samt-Verlauf oben + Vignette */}
        <div className="absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-[#3B0C10]/40 via-black/40 to-transparent pointer-events-none" />
        <div className="absolute inset-0 opacity-[0.05] mix-blend-overlay pointer-events-none"
          style={{ backgroundImage: "radial-gradient(#D4AF37 1px, transparent 1px)", backgroundSize: "3px 3px" }} />

        <div className="relative z-10 min-h-screen grid grid-rows-[auto_1fr_auto] p-8 md:p-12">
          {/* Marquee-Header */}
          <Marquee now={now} />

          {/* Programm-Sektionen */}
          <main ref={scrollRef} className="mt-8 md:mt-12 overflow-hidden">
            {dates.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-[70vh] text-[#A8A398]">
                <Film size={48} className="text-[#D4AF37]/60" />
                <div className="mt-4 font-heading text-3xl">Zurzeit keine Vorstellungen</div>
                <div className="text-xs uppercase tracking-[0.4em] mt-2">Wir sehen uns bald wieder.</div>
              </div>
            ) : (
              <div className="space-y-14">
                {dates.map((date) => (
                  <DaySection key={date} iso={date} shows={byDate[date]} />
                ))}
              </div>
            )}
          </main>

          {/* Ticker unten */}
          <Ticker shows={d.upcoming || []} />
        </div>
      </div>
    </>
  );
}

/* ---------- Header ---------- */
function Marquee({ now }) {
  return (
    <header className="relative">
      {/* Marquee-Glühbirnen */}
      <div className="absolute inset-x-0 -top-2 flex justify-between px-4 pointer-events-none">
        {Array.from({ length: 22 }).map((_, i) => (
          <span key={i} className="w-2 h-2 rounded-full bg-[#D4AF37] shadow-[0_0_10px_rgba(212,175,55,0.9)] animate-bulb" style={{ animationDelay: `${(i % 4) * 220}ms` }} />
        ))}
      </div>

      <div className="pt-6 border-y-2 border-[#D4AF37]/70 py-6 md:py-8 flex items-center justify-between">
        <div className="flex items-center gap-6">
          <CinemaBrand size="lg" stacked={false} />
          <div className="hidden md:block h-14 w-px bg-[#D4AF37]/40" />
          <div className="hidden md:block">
            <div className="text-[10px] uppercase tracking-[0.5em] text-[#D4AF37]/80">Now Showing</div>
            <div className="font-heading italic text-2xl text-[#F5F0E6]/90">Aktuelles Programm</div>
          </div>
        </div>
        <div className="text-right">
          <div className="font-mono text-4xl md:text-6xl tabular-nums text-[#F5F0E6] leading-none">
            {now.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })}
          </div>
          <div className="text-[10px] md:text-xs uppercase tracking-[0.35em] text-[#A8A398] mt-2">
            {now.toLocaleDateString("de-DE", { weekday: "long", day: "2-digit", month: "2-digit", year: "numeric" })}
          </div>
        </div>
      </div>

      <div className="absolute inset-x-0 -bottom-2 flex justify-between px-4 pointer-events-none">
        {Array.from({ length: 22 }).map((_, i) => (
          <span key={i} className="w-2 h-2 rounded-full bg-[#D4AF37] shadow-[0_0_10px_rgba(212,175,55,0.9)] animate-bulb" style={{ animationDelay: `${((i + 2) % 4) * 220}ms` }} />
        ))}
      </div>

      <style>{`
        @keyframes bulb { 0%,100% { opacity: 1; } 50% { opacity: 0.35; } }
        .animate-bulb { animation: bulb 1.8s ease-in-out infinite; }
      `}</style>
    </header>
  );
}

/* ---------- Day-Sektion ---------- */
function DaySection({ iso, shows }) {
  const d = new Date(iso + "T00:00:00");
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const diff = Math.round((d - today) / 86400000);
  const label = diff === 0 ? "Heute" : diff === 1 ? "Morgen" : diff === 2 ? "Übermorgen" : null;
  const weekday = d.toLocaleDateString("de-DE", { weekday: "long" });
  const day = d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });

  // Nach Saal gruppieren
  const halls = Array.from(new Set(shows.map((s) => s.hall || "Saal 1")));
  const multiHall = halls.length > 1;
  const totalShows = shows.length;
  // Wenn viele Filme oder mehrere Säle → Grid-Layout mit kompakten Karten
  const dense = totalShows >= 4 || multiHall;

  return (
    <section>
      {/* Sektions-Header */}
      <div className="flex items-end gap-6 mb-6">
        <div className="shrink-0 text-center w-24">
          <div className="font-heading text-6xl md:text-7xl text-[#D4AF37] leading-none">{d.getDate().toString().padStart(2, "0")}</div>
          <div className="text-[10px] md:text-xs uppercase tracking-[0.32em] text-[#A8A398] mt-1">
            {d.toLocaleDateString("de-DE", { month: "short" }).replace(".", "")}
          </div>
        </div>
        <div className="flex-1 min-w-0 border-l-2 border-[#D4AF37]/40 pl-6">
          <div className="flex items-center gap-3 flex-wrap">
            {label && (
              <span className="text-[10px] uppercase tracking-[0.4em] px-3 py-1 rounded-full border border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/5">
                {label}
              </span>
            )}
            <span className="text-[10px] uppercase tracking-[0.4em] text-[#A8A398]">{weekday}</span>
          </div>
          <div className="mt-1 font-heading text-3xl md:text-4xl">{day}</div>
        </div>
        <div className="hidden md:flex flex-col items-end gap-1 text-right">
          <div className="text-[10px] uppercase tracking-[0.4em] text-[#575148]">{totalShows} {totalShows === 1 ? "Vorstellung" : "Vorstellungen"}</div>
          {multiHall && (
            <div className="text-[10px] uppercase tracking-[0.4em] text-[#D4AF37]/80">{halls.length} Säle</div>
          )}
        </div>
      </div>

      {/* Layout entscheiden */}
      {multiHall ? (
        <div className="space-y-8">
          {halls.map((hall) => (
            <HallGroup key={hall} hall={hall} shows={shows.filter((s) => (s.hall || "Saal 1") === hall)} dense={totalShows >= 4} />
          ))}
        </div>
      ) : dense ? (
        <div className="grid md:grid-cols-2 gap-4">
          {shows.map((s) => <ShowCard key={s.id} s={s} />)}
        </div>
      ) : (
        <div className="space-y-3">
          {shows.map((s) => <ShowRow key={s.id} s={s} />)}
        </div>
      )}
    </section>
  );
}

/* ---------- Saal-Gruppe (bei mehreren Sälen) ---------- */
function HallGroup({ hall, shows, dense }) {
  return (
    <div>
      <div className="flex items-center gap-3 mb-3">
        <div className="flex items-center gap-2 px-4 py-2 rounded-full border-2 border-[#D4AF37]/70 bg-[#D4AF37]/10">
          <MapPin size={14} className="text-[#D4AF37]" />
          <span className="font-heading text-lg text-[#F5F0E6]">{hall}</span>
        </div>
        <div className="flex-1 h-px bg-gradient-to-r from-[#D4AF37]/40 via-[#D4AF37]/10 to-transparent" />
        <span className="text-[10px] uppercase tracking-[0.35em] text-[#575148]">
          {shows.length} {shows.length === 1 ? "Film" : "Filme"}
        </span>
      </div>
      {dense || shows.length >= 4 ? (
        <div className="grid md:grid-cols-2 gap-4">
          {shows.map((s) => <ShowCard key={s.id} s={s} hideHall />)}
        </div>
      ) : (
        <div className="space-y-3">
          {shows.map((s) => <ShowRow key={s.id} s={s} hideHall />)}
        </div>
      )}
    </div>
  );
}

/* ---------- Kompakte Show-Karte (Grid-Layout bei vielen Filmen) ---------- */
function ShowCard({ s, hideHall }) {
  const m = s.movie;
  const ev = eventType(s.event_type);
  const isEvent = s.event_type && s.event_type !== "regular";
  return (
    <div className="relative grid grid-cols-[80px_1fr] gap-4 items-center border border-[#1A1A1A] hover:border-[#D4AF37]/40 bg-[#0A0A0A]/70 backdrop-blur-sm rounded-lg p-3 md:p-4 overflow-hidden">
      {/* Poster */}
      <div className="aspect-[2/3] bg-[#111] rounded overflow-hidden shadow-lg shadow-black/40">
        {m?.poster_url ? (
          <img src={m.poster_url} alt="" onError={(e) => { e.currentTarget.style.display = "none"; }} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[#575148]"><Film size={18} /></div>
        )}
      </div>
      {/* Info */}
      <div className="min-w-0">
        <div className="flex items-baseline gap-3">
          <div className="font-mono text-2xl md:text-3xl tabular-nums text-[#D4AF37] leading-none">{s.start_time}</div>
          {!hideHall && s.hall && (
            <div className="text-[10px] uppercase tracking-[0.32em] text-[#A8A398] flex items-center gap-1">
              <MapPin size={10} className="text-[#D4AF37]" />{s.hall}
            </div>
          )}
        </div>
        <div className="mt-2 flex items-center gap-2 flex-wrap">
          {isEvent && (
            <span className="text-[9px] uppercase tracking-[0.32em] px-2 py-0.5 rounded-full border"
              style={{ color: ev.color, borderColor: ev.color + "80" }}>
              {ev.icon} {ev.l}
            </span>
          )}
          {m?.age_rating && (
            <span className="text-[9px] font-mono border border-[#D4AF37]/70 text-[#D4AF37] px-1.5 py-0.5 rounded">
              {m.age_rating}
            </span>
          )}
        </div>
        <div className="font-heading text-lg md:text-xl text-[#F5F0E6] leading-tight truncate mt-1.5">
          {m?.title_de || m?.title_original || "—"}
        </div>
        <div className="mt-1 text-[10px] uppercase tracking-[0.28em] text-[#A8A398] flex flex-wrap gap-x-3 gap-y-0.5">
          {m?.duration_min ? <span>{m.duration_min} Min</span> : null}
          {s.format && <span>{s.format}</span>}
          <span>Einlass · {s.entry_time}</span>
        </div>
      </div>
    </div>
  );
}

/* ---------- Show-Reihe (Standard-Layout bei wenigen Filmen) ---------- */
function ShowRow({ s, hideHall }) {
  const m = s.movie;
  const ev = eventType(s.event_type);
  const isEvent = s.event_type && s.event_type !== "regular";

  return (
    <div className="relative grid grid-cols-[auto_88px_1fr_auto] md:grid-cols-[auto_112px_1fr_auto] gap-5 md:gap-8 items-center border border-[#1A1A1A] hover:border-[#D4AF37]/40 bg-[#0A0A0A]/70 backdrop-blur-sm rounded-lg p-4 md:p-5 overflow-hidden">
      {/* Startzeit-Chip */}
      <div className="text-center">
        <div className="font-mono text-4xl md:text-6xl tabular-nums text-[#D4AF37] leading-none">{s.start_time}</div>
        <div className="text-[9px] uppercase tracking-[0.35em] text-[#A8A398] mt-2">Einlass · <span className="font-mono">{s.entry_time}</span></div>
      </div>

      {/* Poster */}
      <div className="aspect-[2/3] bg-[#111] rounded overflow-hidden shadow-lg shadow-black/40">
        {m?.poster_url ? (
          <img src={m.poster_url} alt="" onError={(e) => { e.currentTarget.style.display = "none"; }} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[#575148]"><Film size={22} /></div>
        )}
      </div>

      {/* Titel & Meta */}
      <div className="min-w-0">
        {isEvent && (
          <div className="mb-2 inline-block text-[10px] uppercase tracking-[0.32em] px-2 py-0.5 rounded-full border"
            style={{ color: ev.color, borderColor: ev.color + "80" }}>
            {ev.icon} {ev.l}
          </div>
        )}
        <div className="font-heading text-3xl md:text-4xl text-[#F5F0E6] leading-tight truncate">
          {m?.title_de || m?.title_original || "—"}
        </div>
        {m?.title_original && m?.title_de && m.title_original !== m.title_de && (
          <div className="italic text-[#575148] text-xs md:text-sm truncate mt-0.5">{m.title_original}</div>
        )}
        <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs md:text-sm text-[#A8A398]">
          {m?.genre && <span>{m.genre}</span>}
          {m?.duration_min ? <span className="flex items-center gap-1"><Clock size={12} className="text-[#D4AF37]" />{m.duration_min} Min</span> : null}
          {s.language && <span>{s.language}{s.subtitles ? ` · UT ${s.subtitles}` : ""}</span>}
          {s.format && <span>{s.format}</span>}
        </div>
      </div>

      {/* Saal + FSK */}
      <div className="text-right flex flex-col items-end gap-2 shrink-0">
        {!hideHall && (
          <div className="flex items-center gap-2 text-[#F5F0E6]">
            <MapPin size={14} className="text-[#D4AF37]" />
            <span className="font-heading text-xl md:text-2xl">{s.hall}</span>
          </div>
        )}
        {m?.age_rating && (
          <div className="text-[10px] md:text-xs font-mono border border-[#D4AF37]/70 text-[#D4AF37] px-2 py-0.5 rounded">
            {m.age_rating}
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------- Laufband unten + QR zum Ticketautomat ---------- */
function Ticker({ shows }) {
  const items = (shows || []).slice(0, 12).map((s) => `${s.movie?.title_de || "—"} · ${s.start_time} · ${s.hall}`);
  const line = items.length ? items.join("   ·   ") : "Willkommen im Movie House.";
  const kioskUrl = typeof window !== "undefined" ? `${window.location.origin}/kiosk` : "/kiosk";
  return (
    <footer className="mt-8 border-t border-[#1A1A1A] pt-6">
      <div className="grid md:grid-cols-[1fr_auto] gap-6 items-center">
        {/* Ticker links */}
        <div className="min-w-0">
          <div className="overflow-hidden">
            <div className="flex whitespace-nowrap animate-ticker text-[#D4AF37]/85 uppercase tracking-[0.32em] text-xs">
              <span className="px-8">{line}</span>
              <span className="px-8" aria-hidden>{line}</span>
            </div>
          </div>
          <div className="mt-4 flex items-center justify-between text-[10px] uppercase tracking-[0.4em] text-[#575148]">
            <span>THE MOVIE HOUSE · Programm-Board</span>
            <span className="hidden md:inline">tmdb.org (nicht offiziell)</span>
          </div>
        </div>

        {/* QR zum Ticketautomat rechts */}
        <div className="flex items-center gap-4 shrink-0 border-l border-[#1A1A1A] md:pl-6 pt-4 md:pt-0" data-testid="program-kiosk-qr">
          <div className="bg-[#F5F0E6] p-2 rounded">
            <QRCodeCanvas value={kioskUrl} size={92} bgColor="#F5F0E6" fgColor="#0A0A0A" />
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-[0.4em] text-[#D4AF37]">Ticket kaufen</div>
            <div className="font-heading text-2xl md:text-3xl mt-1 leading-none">Jetzt scannen</div>
            <div className="text-[10px] uppercase tracking-[0.32em] text-[#A8A398] mt-2">Direkt zum Ticketautomat</div>
          </div>
        </div>
      </div>
      <style>{`
        @keyframes ticker { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
        .animate-ticker { animation: ticker 45s linear infinite; }
      `}</style>
    </footer>
  );
}
